import React, { useState, useEffect } from 'react';
import { Sidebar } from '../components/Sidebar';
import { PageHeader, StatusBadge, LoadingSpinner, EmptyState } from '../components/UIComponents';
import { Button } from '../components/ui/button';
import { configSummaryAPI } from '../utils/api';
import { formatDecimal } from '../utils/helpers';
import {
  Settings2, Zap, Battery, FileText, ChevronRight, CheckCircle,
  Clock, BarChart3, Calculator, ScrollText, Filter
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const STAGE_COLORS = {
  NEW: 'bg-slate-100 text-slate-700',
  REQUIREMENT_DRAFT: 'bg-blue-100 text-blue-700',
  REQUIREMENT_SUBMITTED: 'bg-blue-200 text-blue-800',
  CONFIGURATION_DRAFT: 'bg-amber-100 text-amber-700',
  CONFIGURATION_SELECTED: 'bg-amber-200 text-amber-800',
  CONFIGURATION_LOCKED: 'bg-green-100 text-green-700',
  PROPOSAL_DRAFT: 'bg-violet-100 text-violet-700',
  PROPOSAL_REVIEW: 'bg-violet-200 text-violet-800',
  PROPOSAL_APPROVED: 'bg-emerald-100 text-emerald-700',
  PROPOSAL_LOCKED: 'bg-emerald-200 text-emerald-800',
  PROPOSAL_SENT: 'bg-cyan-100 text-cyan-700',
  WON: 'bg-green-200 text-green-800',
  LOST: 'bg-red-100 text-red-700',
};

export default function ConfigSummaryPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stageFilter, setStageFilter] = useState('');
  const navigate = useNavigate();

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await configSummaryAPI.getOverview();
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load configuration summary');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="flex"><Sidebar /><div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0"><LoadingSpinner /></div></div>;

  const kpis = data?.kpis || {};
  const runs = data?.runs || [];
  const filtered = stageFilter ? runs.filter(r => r.pipeline_stage === stageFilter) : runs;
  const stages = Object.entries(kpis.pipeline_stages || {}).sort((a, b) => b[1] - a[1]);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-6 lg:p-8 space-y-6">
          <PageHeader title="Configuration Summary" description="Overview of all BESS configurations across the sales pipeline" />

          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="config-kpi-grid">
            <KPICard icon={Settings2} label="Configurator Runs" value={kpis.total_configurator_runs || 0}
              sub={`${kpis.runs_with_selection || 0} with selection`} color="text-amber-600" bg="bg-amber-50" />
            <KPICard icon={Calculator} label="Rental Calculations" value={kpis.total_rental_calcs || 0}
              sub="Financial models" color="text-blue-600" bg="bg-blue-50" />
            <KPICard icon={ScrollText} label="Proposals" value={kpis.total_proposals || 0}
              sub="Draft to sent" color="text-violet-600" bg="bg-violet-50" />
            <KPICard icon={BarChart3} label="Pipeline Sites" value={kpis.total_prospect_sites || 0}
              sub={`${stages.length} stages`} color="text-emerald-600" bg="bg-emerald-50" />
          </div>

          {/* Pipeline Stage Distribution */}
          {stages.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="pipeline-stages">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center">
                <Filter className="w-4 h-4 mr-2 text-slate-400" /> Pipeline Distribution
              </h3>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setStageFilter('')}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${!stageFilter ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                  data-testid="filter-all">
                  All ({runs.length})
                </button>
                {stages.map(([stage, count]) => (
                  <button key={stage} onClick={() => setStageFilter(stageFilter === stage ? '' : stage)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${stageFilter === stage ? 'bg-slate-900 text-white border-slate-900' : `${STAGE_COLORS[stage] || 'bg-slate-100 text-slate-700'} border-transparent`}`}
                    data-testid={`filter-${stage}`}>
                    {stage.replace(/_/g, ' ')} ({count})
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Configurations Table */}
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="config-table">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900">
                Configurations ({filtered.length})
              </h3>
            </div>

            {filtered.length === 0 ? (
              <div className="p-8 text-center">
                <Settings2 className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p className="text-sm text-slate-500">No configurator runs found</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-50">
                {filtered.map((run) => (
                  <div key={run.run_id} className="px-5 py-4 hover:bg-slate-50/50 transition-colors" data-testid={`config-row-${run.run_id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="font-medium text-sm text-slate-900 truncate">{run.company}</span>
                          <span className="text-slate-400">·</span>
                          <span className="text-sm text-slate-600 truncate">{run.site_name}</span>
                          {run.city && <span className="text-xs text-slate-400">({run.city})</span>}
                        </div>
                        <div className="flex items-center space-x-3 text-xs text-slate-500">
                          <span className="flex items-center"><Clock className="w-3 h-3 mr-1" />{run.run_at ? new Date(run.run_at).toLocaleDateString() : 'N/A'}</span>
                          <span className="flex items-center"><Settings2 className="w-3 h-3 mr-1" />{run.options_count} options</span>
                          {run.rental_calcs > 0 && <span className="flex items-center"><Calculator className="w-3 h-3 mr-1" />{run.rental_calcs} calcs</span>}
                          {run.proposals > 0 && <span className="flex items-center"><ScrollText className="w-3 h-3 mr-1" />{run.proposals} proposals</span>}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0 ml-4">
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${STAGE_COLORS[run.pipeline_stage] || 'bg-slate-100 text-slate-600'}`}>
                          {(run.pipeline_stage || 'NEW').replace(/_/g, ' ')}
                        </span>
                        {run.priority === 'HIGH' && <span className="text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">HIGH</span>}
                      </div>
                    </div>

                    {/* Requirement Summary */}
                    {run.requirement && (
                      <div className="flex items-center space-x-4 text-xs text-slate-500 mb-2">
                        {run.requirement.load_kw && <span><Zap className="w-3 h-3 inline mr-0.5 text-amber-500" />{run.requirement.load_kw} kW load</span>}
                        {run.requirement.backup_hours && <span><Battery className="w-3 h-3 inline mr-0.5 text-blue-500" />{run.requirement.backup_hours}h backup</span>}
                        {run.requirement.use_case && <span className="capitalize">{run.requirement.use_case.replace(/_/g, ' ')}</span>}
                        {run.requirement.has_solar && <span className="text-amber-600">Solar</span>}
                      </div>
                    )}

                    {/* Options */}
                    {run.options_summary && run.options_summary.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {run.options_summary.map((opt) => {
                          const isSelected = opt.id === run.selected_option_id;
                          return (
                            <div key={opt.id}
                              className={`text-xs px-3 py-1.5 rounded border ${isSelected ? 'bg-green-50 border-green-300 text-green-800' : 'bg-slate-50 border-slate-200 text-slate-600'}`}
                              data-testid={`option-${opt.id}`}>
                              {isSelected && <CheckCircle className="w-3 h-3 inline mr-1 text-green-600" />}
                              <span className="font-medium">{opt.name}</span>
                              {opt.pcs_kw && <span className="ml-1">{opt.pcs_kw}kW</span>}
                              {opt.battery_kwh && <span>/{opt.battery_kwh}kWh</span>}
                              {opt.confidence != null && <span className="ml-1 opacity-70">({opt.confidence}%)</span>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function KPICard({ icon: Icon, label, value, sub, color, bg }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</span>
        <div className={`w-8 h-8 ${bg} rounded-sm flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${color}`} />
        </div>
      </div>
      <p className="text-2xl font-mono font-bold text-slate-900">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}
